// frontend/src/components/ExpedienteDetallado.js
import React from 'react';
import BotonSubirDocumento from './BotonSubirDocumento';
import ModalVisorDocumento from './expediente/ModalVisorDocumento';
import PanelBiometria from './expediente/PanelBiometria';
import TimelineSoap from './expediente/TimelineSoap';
import CuadriculaPruebas from './expediente/CuadriculaPruebas';
import ModalInformeMedico from './expediente/ModalInformeMedico';
import ModalEditarPaciente from './expediente/ModalEditarPaciente';
import NuevaConsulta from './NuevaConsulta';
import SeccionFacturasPaciente from './SeccionFacturasPaciente';
import EvolucionClinica from './expediente/EvolucionClinica';
import SeccionBonosPaciente from './bonos/SeccionBonosPaciente';
import SeccionPolizasPaciente from './seguros/SeccionPolizasPaciente';

import { useExpedienteDetallado } from '../hooks/useExpedienteDetallado'; // 🚀 IMPORTAMOS EL CEREBRO
import { useToast } from '../context/ToastContext';

export default function ExpedienteDetallado({ pacienteId, onBack }) {
    const { showToast } = useToast();
    const [openEditar, setOpenEditar] = React.useState(false);
    // 1. EXTRAEMOS TODA LA LOGICA POR SRP
    const {
        paciente,
        setPaciente, // 🚀 AÑADIDO: Recibimos la función para actualizar el estado optimista
        consultas, consultaSeleccionada, setConsultaSeleccionada,
        loading, showSkeleton, activeTab, setActiveTab,
        openInforme, setOpenInforme, openNuevaConsulta, setOpenNuevaConsulta,
        visorDoc, bovedaCargando, BASE_URL,
        handleAbrirVisor, handleCerrarVisor,
        handleAtenderAhora, handleProgramarCita,
        calcularEdad, fetchDatosExpediente,
        consultaEnCurso, irAEstacionClinica,
        registrarConsentimiento
    } = useExpedienteDetallado(pacienteId, onBack);

    // 🛡️ CONTROL DE SKELETON (Omitir parpadeos veloces)
    if (loading && !showSkeleton && !paciente) {
        return <div className="min-h-screen bg-slate-50"></div>;
    }

    if (loading && showSkeleton) {
        return (
            <div className="min-h-screen p-8 bg-slate-50 animate-pulse space-y-6">
                <div className="h-64 bg-slate-200 rounded-3xl w-full"></div>
                <div className="h-12 bg-slate-200 rounded-2xl w-full max-w-2xl"></div>
                <div className="h-96 bg-slate-200 rounded-3xl w-full"></div>
            </div>
        );
    }

    if (!paciente) return null;

    // 🛡️ RGPD: recepción ve la ficha administrativa, no la historia clínica
    // (el servidor ya le vacía esos datos; aquí se quitan las secciones).
    const veHistoria = paciente.historiaClinica !== false;
    const pestanas = [
        { id: 'evolutivo', label: 'Evolutivo Médico', clinica: true },
        { id: 'graficas', label: 'Evolución', clinica: true },
        { id: 'bonos', label: 'Bonos' },
        { id: 'seguros', label: 'Seguros' },
        { id: 'biometria', label: 'Dashboard Biométrico', clinica: true },
        { id: 'pruebas', label: veHistoria ? 'Bóveda de Archivos' : 'Documentos' },
        { id: 'facturas', label: 'Historial de Facturas' }
    ].filter(t => veHistoria || !t.clinica);
    const pestanaActiva = pestanas.some(t => t.id === activeTab) ? activeTab : pestanas[0].id;

    const inicialesAvatar = `${paciente.nombre?.[0] || ''}${paciente.apellidos?.[0] || ''}`.toUpperCase();

    return (
        <div className="min-h-screen bg-slate-50 pb-12 animate-fade-in">
            {/* 1. NAVEGACIÓN Y BOTONERA SUPERIOR */}
            <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                <button
                    onClick={onBack}
                    className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-900 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-200 rounded-lg py-1 px-2 -ml-2"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7"></path></svg>
                    Volver al Directorio
                </button>

                <div className="flex flex-wrap gap-2.5">
                    {/* Acción principal: atender al paciente ahora mismo. Si ya hay
                        una consulta en curso, el botón la continúa en vez de abrir otra. */}
                    {veHistoria && <button
                        onClick={consultaEnCurso ? () => irAEstacionClinica() : handleAtenderAhora}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-clinica-primary hover:bg-blue-700 rounded-xl transition-all shadow-md shadow-blue-500/20 focus:outline-none focus:ring-4 focus:ring-clinica-primary/20"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path></svg>
                        {consultaEnCurso ? 'Continuar consulta' : 'Atender ahora'}
                    </button>}

                    {/* Acción secundaria: agendar una cita futura */}
                    <button
                        onClick={handleProgramarCita}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 shadow-sm transition-all"
                    >
                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                        Programar cita
                    </button>

                    {/* Rectificación de datos administrativos (RGPD) */}
                    <button
                        onClick={() => setOpenEditar(true)}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 shadow-sm transition-all"
                    >
                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
                        Editar datos
                    </button>

                    {veHistoria && <button
                        onClick={() => {
                            if (consultas.length > 0) {
                                setConsultaSeleccionada(consultas[0]);
                                setOpenInforme(true);
                            }
                        }}
                        className="px-5 py-2.5 text-sm font-bold text-slate-500 bg-transparent hover:bg-slate-100 rounded-xl transition-all"
                    >
                        Último informe
                    </button>}
                </div>
            </div>

            {/* 🩺 BANNER DE CONSULTA EN CURSO: aparece cuando el paciente está siendo
                atendido ahora mismo. Permite pasar a la Estación Clínica para actualizar
                la consulta tras revisar el historial. */}
            {consultaEnCurso && veHistoria && (
                <div className="sticky top-[73px] z-30 mx-4 sm:mx-6 lg:mx-8 mt-4">
                    <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-2xl shadow-lg shadow-emerald-500/20 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                            <span className="relative flex h-3 w-3 shrink-0">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                            </span>
                            <div className="min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-100">Consulta en curso</p>
                                <p className="text-sm font-bold text-white truncate">
                                    {consultaEnCurso.motivo || 'Acto clínico activo en el box'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => irAEstacionClinica()}
                            className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-emerald-700 hover:bg-emerald-50 text-sm font-black rounded-xl shadow-sm transition-all focus:outline-none focus:ring-4 focus:ring-white/30"
                        >
                            Atender en Estación Clínica
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                        </button>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 space-y-8">
                {/* ⚖️ CONSENTIMIENTO INFORMADO (RGPD / Ley 41/2002) */}
                {paciente.consentimiento?.aceptado ? (
                    <div className="flex items-center gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
                        <svg className="w-5 h-5 text-emerald-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        <p className="text-sm font-bold text-emerald-800">
                            Consentimiento informado aceptado por el paciente
                            <span className="font-semibold text-emerald-600"> · {paciente.consentimiento.fecha ? new Date(paciente.consentimiento.fecha).toLocaleDateString('es-ES') : ''} · {paciente.consentimiento.version || 'RGPD-1'}</span>
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
                        <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                            <p className="text-sm font-semibold text-amber-800">
                                <span className="font-black">Falta el consentimiento informado del paciente.</span> Requerido por RGPD para el tratamiento de sus datos. Regístralo cuando el paciente lo acepte.
                            </p>
                        </div>
                        <button
                            onClick={() => { if (window.confirm('¿Confirmas que el paciente ha aceptado el consentimiento informado? Este registro es inalterable.')) registrarConsentimiento(); }}
                            className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold rounded-xl transition-colors shadow-sm"
                        >
                            Registrar consentimiento
                        </button>
                    </div>
                )}

                {/* 2. HERO BANNER PACIENTE */}
                <div className="relative bg-slate-900 rounded-[2.5rem] p-8 md:p-12 overflow-hidden shadow-2xl flex flex-col md:flex-row items-center gap-8">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-clinica-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

                    <div className="relative z-10 shrink-0">
                        <div className="flex items-center justify-center w-32 h-32 md:w-40 md:h-40 text-4xl md:text-5xl font-black text-white bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-[2.5rem] shadow-xl">
                            {inicialesAvatar}
                        </div>
                        {paciente.activo && (
                            <div className="absolute -bottom-3 -right-3 px-4 py-1.5 bg-emerald-500 border-4 border-slate-900 text-white text-xs font-black rounded-full shadow-lg">
                                ACTIVO
                            </div>
                        )}
                    </div>

                    <div className="relative z-10 text-center md:text-left flex-1 w-full">
                        <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-4">
                            {paciente.nombre} <span className="text-slate-300">{paciente.apellidos}</span>
                        </h1>
                        <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                            <span className="px-4 py-2 bg-white/10 text-white backdrop-blur-md rounded-xl text-sm font-bold border border-white/10 flex items-center gap-2">
                                {paciente.dni}
                            </span>
                            <span className="px-4 py-2 bg-white/10 text-white backdrop-blur-md rounded-xl text-sm font-bold border border-white/10">
                                {calcularEdad(paciente.fechaNacimiento)} años
                            </span>
                            <span className="px-4 py-2 bg-white/10 text-white backdrop-blur-md rounded-xl text-sm font-bold border border-white/10 uppercase">
                                {paciente.seguroMedico || 'PRIVADO'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* 3. MENÚ DE PESTAÑAS */}
                <div className="flex bg-white p-2 rounded-2xl shadow-sm border border-slate-200 overflow-x-auto hide-scrollbar">
                    {pestanas.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 min-w-[155px] py-3.5 px-4 rounded-xl text-sm font-black transition-all focus:outline-none ${pestanaActiva === tab.id ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* 4. SECCIONES DINÁMICAS (SRP aplicado a subcomponentes) */}
                <div className="min-h-[400px]">
                    {pestanaActiva === 'evolutivo' && (
                        <div className="animate-fade-in pt-4">
                            <TimelineSoap consultas={consultas} onVerInforme={(c) => { setConsultaSeleccionada(c); setOpenInforme(true); }} />
                        </div>
                    )}

                    {pestanaActiva === 'graficas' && (
                        <div className="animate-fade-in pt-4">
                            <EvolucionClinica pacienteId={pacienteId} />
                        </div>
                    )}

                    {pestanaActiva === 'bonos' && (
                        <div className="animate-fade-in pt-4">
                            <SeccionBonosPaciente pacienteId={pacienteId} />
                        </div>
                    )}

                    {pestanaActiva === 'seguros' && (
                        <div className="animate-fade-in pt-4">
                            <SeccionPolizasPaciente pacienteId={pacienteId} />
                        </div>
                    )}

                    {pestanaActiva === 'biometria' && (
                        <div className="animate-fade-in">
                            <PanelBiometria paciente={paciente} onActualizar={fetchDatosExpediente} />
                        </div>
                    )}

                    {pestanaActiva === 'pruebas' && (
                        <div className="animate-fade-in bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
                            <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4 border-b border-slate-100 pb-6">
                                <div>
                                    <h3 className="text-xl font-black text-slate-900">{veHistoria ? 'Bóveda de Archivos Clínicos' : 'Documentos del paciente'}</h3>
                                    <p className="text-sm font-medium text-slate-500 mt-1">{veHistoria ? 'Gestión de resultados, pruebas de imagen y analíticas' : 'DNI, consentimientos y documentos generales. Los informes y las pruebas solo los ven los profesionales.'}</p>
                                </div>
                                <div className="flex flex-wrap gap-2.5">
                                    {/* Subida específica de la foto del DNI/NIE */}
                                    <BotonSubirDocumento
                                        pacienteId={pacienteId}
                                        categoria="DNI"
                                        label="Subir foto del DNI"
                                        accept=".jpg,.jpeg,.png,.pdf"
                                        tono="slate"
                                        onUploadSuccess={(nuevoExpediente) => {
                                            setPaciente((prev) => ({ ...prev, expediente: nuevoExpediente }));
                                        }}
                                    />
                                    <BotonSubirDocumento
                                        pacienteId={pacienteId}
                                        onUploadSuccess={(nuevoExpediente) => {
                                            setPaciente((prev) => ({ ...prev, expediente: nuevoExpediente }));
                                        }}
                                    />
                                </div>
                            </div>
                            <CuadriculaPruebas expediente={paciente.expediente || []} baseUrl={BASE_URL} loading={bovedaCargando} onPreview={handleAbrirVisor} />
                        </div>
                    )}

                    {pestanaActiva === 'facturas' && (
                        <div className="animate-fade-in bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
                            <div className="mb-6 border-b border-slate-100 pb-4">
                                <h3 className="text-xl font-black text-slate-900">Historial Comercial y Recibos</h3>
                                <p className="text-sm font-medium text-slate-500 mt-1">Auditoría contable individual, recibos y facturas expedidas</p>
                            </div>
                            <SeccionFacturasPaciente pacienteId={pacienteId} />
                        </div>
                    )}
                </div>
            </div>

            {/* =========================================================
                📦 CAPA DE MODALES Y DRAWER FLOTANTES
               ========================================================= */}
            {openInforme && (
                <ModalInformeMedico consulta={consultaSeleccionada || consultas[0]} paciente={paciente} onClose={() => { setOpenInforme(false); setConsultaSeleccionada(null); }} />
            )}

            {openNuevaConsulta && (
                <NuevaConsulta open={openNuevaConsulta} onClose={() => setOpenNuevaConsulta(false)} pacienteId={pacienteId} onSuccess={fetchDatosExpediente} />
            )}

            <ModalVisorDocumento isOpen={visorDoc.isOpen} onClose={handleCerrarVisor} blobUrl={visorDoc.blobUrl} mimetype={visorDoc.mimetype} cargando={visorDoc.cargando} />

            {openEditar && (
                <ModalEditarPaciente
                    open={openEditar}
                    onClose={() => setOpenEditar(false)}
                    paciente={paciente}
                    onSuccess={fetchDatosExpediente}
                    mostrarToast={showToast}
                />
            )}
        </div>
    );
}